"use client"

import { TransferItemIcon } from "@/app/icons/transfer-item-icon"
import { useSwitchEvmChain } from "@/app/sdk/use-switch-evm-chain"
import { useWalletConnect } from "@/app/wallet-connect/hooks"
import { Trans } from "@lingui/react/macro"
import type { Address } from "@rarible_int/anaconda_sdk/ethereum"
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@rarible_int/anaconda_ui_components"
import { wrap } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { type FormEvent, useCallback, useEffect, useState } from "react"
import { useConnectStatus } from "../order/widget/actions/use-connect-status"
import { useNftReatom } from "../store/hooks.reatom"
import { NftMoleculeReatom, type TransferArgs } from "../store/nft-store.reatom"

export const TransferItemButtonReatom = reatomComponent(() => {
  const nft = useNftReatom()
  const { transferNftAction } = useMolecule(NftMoleculeReatom)
  const { status } = useWalletConnect()

  // Get action status from withAsync
  const isPending = transferNftAction.pending() > 0
  const error = transferNftAction.error()

  const [open, setOpen] = useState(false)
  const [address, setAddress] = useState("")

  const onSuccess = useCallback(
    (args: TransferArgs) => wrap(() => transferNftAction(args))(),
    [transferNftAction],
  )
  const { action, switchInProgress } = useSwitchEvmChain<TransferArgs>(
    nft.collection.blockchain,
    onSuccess,
  )

  const isLoading = switchInProgress || isPending

  const func = useConnectStatus(() => {
    setOpen(true)
  })

  const transferNftHandler = useCallback(
    (e: FormEvent) => {
      e.preventDefault()

      const to = address.trim().toLowerCase() as Address
      action({ nft, to })
    },
    [action, nft, address],
  )

  const onOpenChange = useCallback((isOpen: boolean) => {
    setAddress("")
    setOpen(isOpen)
  }, [])

  // Close dialog and reset form on error
  useEffect(() => {
    if (error) {
      setOpen(false)
      setAddress("")
    }
  }, [error])

  return (
    <>
      <Tooltip>
        <TooltipTrigger
          className="cursor-pointer"
          onClick={() => {
            if (status === "connected") {
              setOpen(true)
            } else {
              func()
            }
          }}
          asChild
        >
          <Button size="icon" variant="ghost">
            <TransferItemIcon className="[&&]:h-6 [&&]:w-6" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <Trans>Transfer Item</Trans>
        </TooltipContent>
      </Tooltip>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[400px] rounded-xl border border-neutral-800 bg-black p-6">
          <form onSubmit={transferNftHandler}>
            <DialogHeader className="mb-2">
              <DialogTitle className="font-semibold text-lg text-white">
                <Trans>Transfer</Trans>
              </DialogTitle>
            </DialogHeader>
            <div className="mb-5 text-sm">
              <Trans>You can transfer items from your address to another</Trans>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="receiver-address">Receiver address</Label>
              <Input
                id="receiver-address"
                className="mb-6 border border-neutral-700 bg-neutral-900 text-white placeholder:text-neutral-500"
                placeholder="Paste address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                className="w-full rounded-md bg-brand py-3 font-medium text-base text-black disabled:opacity-60"
                disabled={!address.trim()}
                loading={isLoading}
              >
                {isLoading ? null : <Trans>Continue</Trans>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
})
